import { useSelector } from "react-redux";
import ResultDial from "../../ResultDial";
import RaceweekFlag from "../../Cloudinary/RaceweekFlag";
import { boosterIdKey } from "../../../Common/constants";
import { BoosterChips } from "../../BoosterChips";
import LeagueTeamName from "../../LeagueTeamName";
import StatisticsImage from "../../Cloudinary/StatisticsImage";
import {
  getBoosterCategory,
  getPlayerFromPlayerId,
} from "../../../Common/utils";

/**
 * COUNTRY SCOREBOARD LAYOUT REFERENCE GUIDE
 * ---------------------------------------------------------------------------------
 * This component handles COUNTRY type scoreboards (race-by-race comparisons).
 * All grid columns are defined using fractional units (fr) for fluid scaling.
 *
 * | Layout Type | Image Ref | Description               | layoutConfig                                                  |
 * |-------------|-----------|---------------------------|---------------------------------------------------------------|
 * | Country 1   | Image 5-A | Country Header Top        | { variant: "top-header" }                                     |
 * | Country 2   | Image 6   | Country Header Inline     | { variant: "inline-header" }                                  |
 * | Country 3   | Image 5-C | Country Horizontal Full   | { variant: "inline-header", containerClass: "full-width" }    |
 * ---------------------------------------------------------------------------------
 */

/**
 * @param {Object} layoutConfig - Configuration object for layout styling
 * @param {string} layoutConfig.containerClass - Additional CSS class for the container (e.g., "full-width")
 * @param {string} layoutConfig.variant - "top-header" (Country above) | "inline-header" (Country between teams with badges)
 * @param {Object} countryResult - The country/race battle result data
 * @param {Array} countryResult.members - Array of two members participating in the battle
 * @param {string} countryResult.battle_result - Result indicator ("W", "L", "D")
 * @param {string} countryResult.round_name - Name of the race/round (e.g., "Australian Grand Prix")
 * @param {number} countryResult.round_id - ID of the race/round
 * @param {number} countryResult.battle_result_percentage - Battle result percentage
 */
const CountryScoreboardContainer = ({
  layoutConfig = {},
  countryResult = {},
  handleHeadToHeadViewGamedayIdChange = () => {},
}) => {
  const { containerClass = "", variant = "inline-header" } = layoutConfig;
  const fixtureCircuitConfigState = useSelector(
    (state) => state.fixtureCircuitConfig
  );
  const driverState = useSelector((state) => state.driver);

  const {
    members = [],
    battle_result = "",
    round_name = "",
    round_id = "",
    battle_result_percentage = 0,
  } = countryResult;

  const isInline = variant === "inline-header";

  // Find the current user and opponent from members array
  const currentUser = members?.find((member) => member?.is_current_user === 1);
  const opponent = members?.find((member) => member?.is_current_user === 0);

  // Determine result status for the dial
  const resultStatus = battle_result?.toLowerCase() || "";

  const userTeamDrivers = currentUser?.user_team
    ?.map((playerId) => getPlayerFromPlayerId(driverState, playerId))
    ?.filter((player) => player?.PositionName === "DRIVER");
  const opponentTeamDrivers = opponent?.user_team
    ?.map((playerId) => getPlayerFromPlayerId(driverState, playerId))
    ?.filter((player) => player?.PositionName === "DRIVER");

  // Split drivers into two rows (first 3 in row 1, remaining in row 2)
  const userTeamDriversRow1 = userTeamDrivers?.slice(0, 3);
  const userTeamDriversRow2 = userTeamDrivers?.slice(3);
  const opponentTeamDriversRow1 = opponentTeamDrivers?.slice(0, 3);
  const opponentTeamDriversRow2 = opponentTeamDrivers?.slice(3);
  const userTeamConstructors = currentUser?.user_team
    ?.map((playerId) => getPlayerFromPlayerId(driverState, playerId))
    ?.filter((player) => player?.PositionName === "CONSTRUCTOR");
  const opponentTeamConstructors = opponent?.user_team
    ?.map((playerId) => getPlayerFromPlayerId(driverState, playerId))
    ?.filter((player) => player?.PositionName === "CONSTRUCTOR");

  const fixtures = fixtureCircuitConfigState?.data?.fixtures;
  const fixture = fixtures?.find(
    (fixtureItem) => fixtureItem?.GamedayId === round_id
  );

  return (
    <div
      className={`si-scoreboard si-type-country si-variant-${variant} ${containerClass}`}
      onClick={() => handleHeadToHeadViewGamedayIdChange(round_id)}
    >
      {/* COUNTRY HEADER: Always shown for country type */}
      <div className="si-country-header">
        {/* Show badges in header only for inline-header variant */}
        {isInline && (
          <div className="si-header-badge si-left">
            <LeagueTeamName
              teamNumber={currentUser?.team_no}
              teamName={"MY TEAM"}
              showSerialNumber={false}
            />
          </div>
        )}

        <div className="si-header-center">
          <span className="si-round">R{round_id}</span>
          <span className="si-flag">
            {/* Flag component can be added here */}
            <RaceweekFlag size="md" countryName={fixture?.CountryName} />
          </span>
          <h3 className="si-name">{fixture?.CountryName}</h3>
        </div>

        {/* Show badges in header only for inline-header variant */}
        {isInline && (
          <div className="si-header-badge si-right">
            <LeagueTeamName
              teamNumber={opponent?.team_no}
              teamName={"OPPONENT"}
              showSerialNumber={false}
              isOpponentTeam={true}
            />
          </div>
        )}
      </div>

      <div className="si-main-grid">
        {/* LEFT GROUP - Current User's Team */}
        <div className="si-col-team si-left">
          {/* Show badge in team column only for top-header variant */}
          {!isInline && (
            <div className="si-badge">
              <LeagueTeamName
                teamNumber={currentUser?.team_no}
                teamName={"MY TEAM"}
                showSerialNumber={false}
              />
            </div>
          )}
          <div className="si-avatar-list">
            {/* Avatar circles/rects for team members can be rendered here */}

            {variant === "top-header" ? (
              <>
                <div className="si-avatar-list-row">
                  {userTeamDriversRow1?.map((driver, index) => (
                    <div
                      key={`left-avatar-${index}`}
                      className="si-avatar-item"
                    >
                      <StatisticsImage statisticsPlayer={driver} />
                    </div>
                  ))}
                </div>
                <div className="si-avatar-list-row">
                  {userTeamDriversRow2?.map((driver, index) => (
                    <div
                      key={`left-avatar-${index}`}
                      className="si-avatar-item"
                    >
                      <StatisticsImage statisticsPlayer={driver} />
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="si-avatar-list-row">
                  {userTeamDrivers?.map((driver, index) => (
                    <div
                      key={`left-avatar-${index}`}
                      className="si-avatar-item"
                    >
                      <StatisticsImage statisticsPlayer={driver} />
                    </div>
                  ))}
                </div>
              </>
            )}
            <div className="si-avatar-list-row">
              {userTeamConstructors?.map((constructor, index) => (
                <div key={`left-avatar-${index}`} className="si-avatar-item">
                  <StatisticsImage statisticsPlayer={constructor} />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="si-col-booster si-left">
          {currentUser?.booster_id > 0 && (
            <BoosterChips
              boosterCls={
                getBoosterCategory(boosterIdKey[currentUser?.booster_id])
                  ?.boosterCls
              }
              boosterIcon={
                getBoosterCategory(boosterIdKey[currentUser?.booster_id])
                  ?.boosterIcon
              }
            />
          )}
        </div>

        <div className="si-col-score si-left">
          {currentUser?.points?.toFixed(0) ?? "000"}
        </div>

        {/* CENTER DIAL */}
        <div className="si-col-dial">
          <div className="si-col-divider" />
          <ResultDial
            size="small"
            percentage={battle_result_percentage || 0}
            status={resultStatus}
            text={battle_result || ""}
          />
          <div className="si-col-divider" />
        </div>

        <div className="si-col-score si-right">
          {opponent?.points?.toFixed(0) ?? "000"}
        </div>

        <div className="si-col-booster si-right">
          {opponent?.booster_id > 0 && (
            <BoosterChips
              boosterCls={
                getBoosterCategory(boosterIdKey[opponent?.booster_id])
                  ?.boosterCls
              }
              boosterIcon={
                getBoosterCategory(boosterIdKey[opponent?.booster_id])
                  ?.boosterIcon
              }
            />
          )}
        </div>

        {/* RIGHT GROUP - Opponent's Team */}
        <div className="si-col-team si-right">
          {/* Show badge in team column only for top-header variant */}
          {!isInline && (
            <div className="si-badge">
              <LeagueTeamName
                teamNumber={opponent?.team_no}
                teamName={"OPPONENT"}
                isOpponentTeam={true}
                showSerialNumber={false}
              />
            </div>
          )}
          <div className="si-avatar-list">
            {/* Avatar circles/rects for team members can be rendered here */}
            {variant === "top-header" ? (
              <>
                <div className="si-avatar-list-row">
                  {opponentTeamDriversRow1?.map((driver, index) => (
                    <div
                      key={`right-avatar-${index}`}
                      className="si-avatar-item"
                    >
                      <StatisticsImage statisticsPlayer={driver} />
                    </div>
                  ))}
                </div>
                <div className="si-avatar-list-row">
                  {opponentTeamDriversRow2?.map((driver, index) => (
                    <div
                      key={`right-avatar-${index}`}
                      className="si-avatar-item"
                    >
                      <StatisticsImage statisticsPlayer={driver} />
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="si-avatar-list-row">
                  {opponentTeamDrivers?.map((driver, index) => (
                    <div
                      key={`right-avatar-${index}`}
                      className="si-avatar-item"
                    >
                      <StatisticsImage statisticsPlayer={driver} />
                    </div>
                  ))}
                </div>
              </>
            )}
            <div className="si-avatar-list-row">
              {opponentTeamConstructors?.map((constructor, index) => (
                <div key={`right-avatar-${index}`} className="si-avatar-item">
                  <StatisticsImage statisticsPlayer={constructor} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CountryScoreboardContainer;
